import { HttpClient } from "@angular/common/http";
import { Component, Renderer2, ViewChild } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort, MatSortable, Sort } from "@angular/material/sort";
import { ERTEducation } from "../../interfaces/ert-education/ert-education";
import { EducationService } from "../../services/education/education.service";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { IPicklistValuesData } from "../../interfaces/administration/picklistValuesData";
import { PicklistItem } from "../../shared/picklist/picklist-select.component";
import { FireDepartmentsBcTurnoversService } from "../../services/firedepartments/fireDepartmentsBcTurnovers.service";
import { PicklistService } from "../../services/administration/picklist/picklist.service";
import { ILoggedUserData } from "../../interfaces/common/loggedUserData";
import { LoggedUserService } from "../../services/common/logged-user.service";

@Component({
  selector: "ert-education-form-component",
  templateUrl: "./ert-education-form.component.html",
})
export class ERTEducationFormComponent {
  name: string;
  title = "ERT Education";
  sortedBy: string;
  totalItems: number;
  currentUser: ERTEducation;
  currentLoggedInUser: ILoggedUserData;
  isUserUpdating = false;
  educationForm: FormGroup;
  validationMessages: string[] = [];
  userId: number;
  schoolDivisionId: number = 0;
  schoolAttendedId: number = 0;

  selectedSchoolAttendedItem!: PicklistItem;
  selectedSchoolDivisionItem!: PicklistItem;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private renderer: Renderer2,
    private pickListsService: PicklistService,
    private bcTurnOverService: FireDepartmentsBcTurnoversService,
    private educationService: EducationService,
    private fb: FormBuilder,
    private loggedUserService: LoggedUserService
  ) {
    this.route.queryParams.subscribe((q) => {
      this.name = q["name"];
      this.userId = +q["userId"];
    });
    this.educationForm = this.fb.group({
      schoolAttended: ["-1", Validators.required],
      schoolDivision: ["-1", Validators.required],
      attendanceDate: ["", Validators.required],
      hours: ["", Validators.required],
      onOffSite: ["", Validators.required],
      registrationNumber: [""],
      recertDate: [""],
      comments: [""],
    });
  }

  displayedColumns: Map<string, string> = new Map<string, string>([
    ["schoolAttended", "School Attended"],
    ["schoolDivision", "School Division"],
    ["attendanceDate", "Attendance Date"],
    ["recertDate", "Recert Date"],
  ]);

  dataSource = new MatTableDataSource<ERTEducation>();
  displayedColumnsArray: string[] = Array.from(this.displayedColumns.keys());
  pageSize = 10;

  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.sort.sort({ id: "ertEducationId", start: "asc" } as MatSortable);
    this.sort.sortChange.subscribe((sortState: Sort) => {
      this.onSortChange(sortState);
    });
    this.sortedBy = `Sorted by ${this.displayedColumns.get("ertEducationId")}`;
    this.dataSource.paginator = this.paginator;
  }

  ngOnInit(): void {
    this.loadEducations();
    this.pickListsService
      .getFieldIdByName("school Division")
      .subscribe((schoolDivisionId) => {
        if (schoolDivisionId) this.schoolDivisionId = schoolDivisionId;
      });
    this.pickListsService
      .getFieldIdByName("school attended")
      .subscribe((schoolAttendedId) => {
        if (schoolAttendedId) this.schoolAttendedId = schoolAttendedId;
      });
  }

  loadEducations() {
    this.educationService
      .getUserEducationByUserId(this.userId)
      .subscribe((data) => {
        console.log("data", data);
        data.forEach((item) => {
          item.attendanceDate = this.formatDate(new Date(item.attendanceDate));
        });
        this.dataSource.data = data;
        this.totalItems = data.length;
      });
  }

  getLastPostedShoolAttended(picklistValues: IPicklistValuesData[]): string {
    let name: string = this.bcTurnOverService.getValueForField(
      picklistValues,
      this.schoolAttendedId
    );
    return name;
  }

  getLastPostedShoolDivision(picklistValues: IPicklistValuesData[]): string {
    let name: string = this.bcTurnOverService.getValueForField(
      picklistValues,
      this.schoolDivisionId
    );
    return name;
  }

  formatDate(date: Date): string {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  }

  onSortChange(sortState: Sort) {
    if (sortState.direction.length > 0) {
      this.sortedBy = `Sorted by ${this.displayedColumns.get(
        sortState.active
      )}`;
    } else {
      this.sortedBy = "Not sorted";
    }
  }

  getDisplayedItemsRange(): string {
    if (!this.paginator) {
      return "";
    }
    const startIndex = this.paginator.pageIndex * this.paginator.pageSize + 1;
    const endIndex = Math.min(
      (this.paginator.pageIndex + 1) * this.paginator.pageSize,
      this.totalItems
    );
    return ` ${endIndex}`;
  }

  onSchoolAttendedSelected(item: PicklistItem) {
    this.selectedSchoolAttendedItem = item;
  }

  onSchoolDivisionSelected(item: PicklistItem) {
    this.selectedSchoolDivisionItem = item;
  }

  private scrollToTop() {
    this.renderer.setProperty(document.documentElement, "scrollTop", 0);
    this.renderer.setProperty(document.body, "scrollTop", 0);
  }

  saveEducation() {
    this.validationMessages = this.educationService.validateForm(
      this.educationForm
    );
  
    const schoolAttendedValue = this.educationForm.get("schoolAttended")?.value;
    if (
      !this.selectedSchoolAttendedItem ||
      this.selectedSchoolAttendedItem.id !== schoolAttendedValue
    ) {
      this.educationForm.get("schoolAttended")?.setErrors({ required: true });
    }

    if (this.educationForm.invalid) {
      this.scrollToTop();
      return;
    }
  
    const schoolAttended: IPicklistValuesData = {
      id: schoolAttendedValue,
      value: this.selectedSchoolAttendedItem.text,
      picklistFieldId: this.selectedSchoolAttendedItem.fieldId,
      facilityId: Number(this.currentLoggedInUser?.facilityId) || 1,
    };
  
    const schoolDivisionValue = this.educationForm.get("schoolDivision")?.value;
    if (
      !this.selectedSchoolDivisionItem ||
      this.selectedSchoolDivisionItem.id !== schoolDivisionValue
    ) {
      this.educationForm.get("schoolDivision")?.setErrors({ required: true });
    }
  
    const schoolDivision: IPicklistValuesData = {
      id: schoolDivisionValue,
      value: this.selectedSchoolDivisionItem.text,
      picklistFieldId: this.selectedSchoolDivisionItem.fieldId,
      facilityId: Number(this.currentLoggedInUser?.facilityId) || 1,
    };

    this.loggedUserService.getLoggedUserData().subscribe((user) => {
      this.currentLoggedInUser = user;
    });  
    const isQualifiedForErtRoster = schoolAttended.value.includes("**");
    this.isUserUpdating = true;
    const education: ERTEducation = {
      ...this.educationForm.value,
      id: this.currentUser?.id || 0,
      facilityId: Number(this.currentLoggedInUser?.facilityId) || 1,
      userErtId: this.userId,
      onOffSite: this.educationForm.value.onOffSite === "1" ? 1 : 2,
      schoolAttendedId: schoolAttendedValue,
      schoolDivisionId: schoolDivisionValue,
      recertDate: this.educationForm.value.recertDate || null,
      isQualifiedForErtRoster: isQualifiedForErtRoster,
    };  
    this.educationService.saveEducation(education).subscribe({
      next: (savedUser: ERTEducation) => {
        this.currentUser = savedUser;
        this.isUserUpdating = false;
        this.router.navigate(["/ert-education"]);
      },
    });
  }
}
